/**
 * Lightweight API Client Utility
 *
 * Handles API URL resolution across different deployment environments.
 * Works in both the web client and static public pages (Cloudflare Pages).
 */

import { config } from '../../config/runtime-config';

/**
 * Get the appropriate API base URL for the current environment
 */
export function getApiBaseUrl(): string {
  return config.apiUrl;
}

/**
 * Get the health endpoint URL
 * Replaces /api/v2 with /api/health for health checks
 */
export function getHealthUrl(): string {
  const baseUrl = getApiBaseUrl();
  return baseUrl.replace('/api/v2', '/api/health');
}

/**
 * Build a full API URL from a relative path
 * @param path - Relative API path (e.g., '/users/me')
 */
export function buildApiUrl(path: string): string {
  const baseUrl = getApiBaseUrl();
  // Remove leading slash from path if present to avoid double slashes
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${baseUrl}/${cleanPath}`;
}

/**
 * Check if we're in a development environment (using webpack proxy)
 */
export function isDevelopment(): boolean {
  return !getApiBaseUrl().includes('http');
}

/**
 * Simple fetch wrapper with environment-aware URL handling
 */
export async function apiFetch<T = any>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = endpoint.startsWith('http')
    ? endpoint
    : buildApiUrl(endpoint);

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`API request failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
}
